import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Wallet, Plus, Trash2, TrendingUp, TrendingDown, PiggyBank, Target, Lock, Calendar } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FinancePage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [newTransaction, setNewTransaction] = useState({
    type: 'expense',
    amount: '',
    category: 'other',
    description: ''
  });
  const [newGoal, setNewGoal] = useState({ title: '', target_amount: '', current_amount: 0 });
  const [showGoalForm, setShowGoalForm] = useState(false);

  useEffect(() => {
    if (hasAccess) {
      fetchContent();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchContent = async () => {
    try {
      const [transRes, goalsRes, tipsRes] = await Promise.all([
        axios.get(`${API}/api/finance/transactions`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/finance/goals`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/finance/tips`, { headers: getAuthHeader() })
      ]);
      setTransactions(transRes.data);
      setGoals(goalsRes.data);
      setTips(tipsRes.data);
    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTransaction = async () => {
    if (!newTransaction.amount || !newTransaction.description) return;
    try {
      await axios.post(`${API}/api/finance/transactions`, {
        ...newTransaction,
        amount: parseFloat(newTransaction.amount)
      }, { headers: getAuthHeader() });
      setNewTransaction({ type: 'expense', amount: '', category: 'other', description: '' });
      setShowForm(false);
      fetchContent();
    } catch (error) {
      console.error('Error adding transaction:', error);
    }
  };

  const deleteTransaction = async (id) => {
    try {
      await axios.delete(`${API}/api/finance/transactions/${id}`, { headers: getAuthHeader() });
      fetchContent();
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };

  const addGoal = async () => {
    if (!newGoal.title || !newGoal.target_amount) return;
    try {
      await axios.post(`${API}/api/finance/goals`, {
        ...newGoal,
        target_amount: parseFloat(newGoal.target_amount)
      }, { headers: getAuthHeader() });
      setNewGoal({ title: '', target_amount: '', current_amount: 0 });
      setShowGoalForm(false);
      fetchContent();
    } catch (error) {
      console.error('Error adding goal:', error);
    }
  };

  const updateGoalProgress = async (id, amount) => {
    try {
      await axios.put(`${API}/api/finance/goals/${id}`, { add_amount: amount }, { headers: getAuthHeader() });
      fetchContent();
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const categories = {
    food: { label: 'Mat', icon: '🍽️' },
    transport: { label: 'Transport', icon: '🚗' },
    housing: { label: 'Boende', icon: '🏠' },
    entertainment: { label: 'Nöje', icon: '🎬' },
    health: { label: 'Hälsa', icon: '💊' },
    shopping: { label: 'Shopping', icon: '🛍️' },
    income: { label: 'Inkomst', icon: '💰' },
    other: { label: 'Övrigt', icon: '📦' }
  };

  // Calculate totals
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthTransactions = transactions.filter(t => t.created_at.startsWith(thisMonth));
  const totalIncome = monthTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalExpenses = monthTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
  const balance = totalIncome - totalExpenses;

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="finance-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-2xl font-light">Ekonomi</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/50" />
            <h2 className="font-heading text-2xl">Uppgradera för tillgång</h2>
            <p className="text-muted-foreground">Ekonomihantering ingår i premium.</p>
            <Button onClick={() => navigate('/subscription')} className="rounded-full" data-testid="upgrade-btn">
              Uppgradera nu
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="finance-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-2xl font-light">Ekonomi</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Intro */}
        <div className="text-center space-y-3 animate-fade-in">
          <Wallet className="w-12 h-12 mx-auto text-primary" />
          <h2 className="font-heading text-3xl font-light">Ekonomisk balans</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Få kontroll över din ekonomi. Spåra utgifter, sätt sparmål och minska ekonomisk stress.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 justify-center flex-wrap">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className="rounded-full"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Översikt
          </Button>
          <Button
            variant={activeTab === 'goals' ? 'default' : 'outline'}
            onClick={() => setActiveTab('goals')}
            className="rounded-full"
          >
            <Target className="w-4 h-4 mr-2" />
            Sparmål
          </Button>
          <Button
            variant={activeTab === 'tips' ? 'default' : 'outline'}
            onClick={() => setActiveTab('tips')}
            className="rounded-full"
          >
            <PiggyBank className="w-4 h-4 mr-2" />
            Tips
          </Button>
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Monthly Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-card rounded-2xl border border-border/50 p-4 text-center animate-slide-up">
                <TrendingUp className="w-6 h-6 mx-auto text-green-500 mb-2" />
                <p className="text-lg font-heading font-medium text-green-600">+{totalIncome.toLocaleString()} kr</p>
                <p className="text-xs text-muted-foreground">Inkomst</p>
              </div>
              <div className="bg-card rounded-2xl border border-border/50 p-4 text-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
                <TrendingDown className="w-6 h-6 mx-auto text-red-500 mb-2" />
                <p className="text-lg font-heading font-medium text-red-600">-{totalExpenses.toLocaleString()} kr</p>
                <p className="text-xs text-muted-foreground">Utgifter</p>
              </div>
              <div className="bg-card rounded-2xl border border-border/50 p-4 text-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
                <Wallet className="w-6 h-6 mx-auto text-primary mb-2" />
                <p className={`text-lg font-heading font-medium ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {balance >= 0 ? '+' : ''}{balance.toLocaleString()} kr
                </p>
                <p className="text-xs text-muted-foreground">Balans</p>
              </div>
            </div>

            {/* Add Transaction */}
            {!showForm && (
              <Button
                onClick={() => setShowForm(true)}
                className="w-full rounded-xl py-6"
                variant="outline"
                data-testid="add-transaction-toggle"
              >
                <Plus className="w-5 h-5 mr-2" />
                Lägg till transaktion
              </Button>
            )}

            {showForm && (
              <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 animate-fade-in">
                <h3 className="font-heading text-lg font-medium">Ny transaktion</h3>
                
                <div className="flex gap-2">
                  <Button
                    variant={newTransaction.type === 'expense' ? 'default' : 'outline'}
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'expense' })}
                    className="flex-1 rounded-xl"
                  >
                    <TrendingDown className="w-4 h-4 mr-2" />
                    Utgift
                  </Button>
                  <Button
                    variant={newTransaction.type === 'income' ? 'default' : 'outline'}
                    onClick={() => setNewTransaction({ ...newTransaction, type: 'income' })}
                    className="flex-1 rounded-xl"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Inkomst
                  </Button>
                </div>

                <input
                  type="number"
                  value={newTransaction.amount}
                  onChange={(e) => setNewTransaction({ ...newTransaction, amount: e.target.value })}
                  placeholder="Belopp (kr)"
                  className="w-full p-4 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="amount-input"
                />

                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(categories).filter(([key]) => key !== 'income').map(([key, { label, icon }]) => (
                    <button
                      key={key}
                      onClick={() => setNewTransaction({ ...newTransaction, category: key })}
                      className={`p-2 rounded-xl border text-center text-sm transition-colors ${
                        newTransaction.category === key
                          ? 'border-primary bg-primary/10'
                          : 'border-border/50 hover:bg-secondary/50'
                      }`}
                    >
                      <span className="text-lg">{icon}</span>
                      <p className="text-xs mt-1">{label}</p>
                    </button>
                  ))}
                </div>

                <input
                  type="text"
                  value={newTransaction.description}
                  onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                  placeholder="Beskrivning"
                  className="w-full p-4 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="description-input"
                />

                <div className="flex gap-2">
                  <Button onClick={addTransaction} className="flex-1 rounded-xl" data-testid="save-transaction-btn">
                    Spara
                  </Button>
                  <Button onClick={() => setShowForm(false)} variant="outline" className="rounded-xl">
                    Avbryt
                  </Button>
                </div>
              </div>
            )}

            {/* Transactions List */}
            <section className="space-y-4">
              <h3 className="font-heading text-xl font-medium flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Transaktioner
              </h3>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-20 bg-secondary/50 rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : transactions.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Inga transaktioner ännu. Börja med att lägga till din första!
                </p>
              ) : (
                <div className="space-y-3">
                  {transactions.slice(0, 20).map((trans, index) => (
                    <div
                      key={trans.id}
                      className="bg-card rounded-2xl border border-border/50 p-4 flex items-center gap-4 animate-slide-up"
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`transaction-${trans.id}`}
                    >
                      <span className="text-2xl">{categories[trans.category]?.icon || '📦'}</span>
                      <div className="flex-1">
                        <p className="font-medium">{trans.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(trans.created_at).toLocaleDateString('sv-SE')} • {categories[trans.category]?.label}
                        </p>
                      </div>
                      <p className={`font-heading font-medium ${trans.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                        {trans.type === 'income' ? '+' : '-'}{trans.amount.toLocaleString()} kr
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteTransaction(trans.id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {activeTab === 'goals' && (
          <section className="space-y-6">
            {!showGoalForm && (
              <Button
                onClick={() => setShowGoalForm(true)}
                className="w-full rounded-xl py-6"
                variant="outline"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nytt sparmål
              </Button>
            )}

            {showGoalForm && (
              <div className="bg-card rounded-2xl border border-border/50 p-6 space-y-4 animate-fade-in">
                <h3 className="font-heading text-lg font-medium">Nytt sparmål</h3>
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                  placeholder="Vad sparar du till? (t.ex. Semester)"
                  className="w-full p-4 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <input
                  type="number"
                  value={newGoal.target_amount}
                  onChange={(e) => setNewGoal({ ...newGoal, target_amount: e.target.value })}
                  placeholder="Målbelopp (kr)"
                  className="w-full p-4 rounded-xl border border-border/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <div className="flex gap-2">
                  <Button onClick={addGoal} className="flex-1 rounded-xl">Spara</Button>
                  <Button onClick={() => setShowGoalForm(false)} variant="outline" className="rounded-xl">Avbryt</Button>
                </div>
              </div>
            )}

            {goals.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Inga sparmål ännu. Skapa ditt första mål!
              </p>
            ) : (
              <div className="space-y-4">
                {goals.map((goal, index) => {
                  const progress = (goal.current_amount / goal.target_amount) * 100;
                  return (
                    <div
                      key={goal.id}
                      className="bg-card rounded-2xl border border-border/50 p-6 animate-slide-up"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-heading text-lg font-medium">{goal.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {goal.current_amount.toLocaleString()} av {goal.target_amount.toLocaleString()} kr
                          </p>
                        </div>
                        <PiggyBank className="w-8 h-8 text-primary" />
                      </div>
                      <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">{progress.toFixed(0)}% av målet</p>
                      <div className="flex gap-2 mt-4">
                        {[100, 500, 1000].map(amount => (
                          <Button
                            key={amount}
                            variant="outline"
                            size="sm"
                            onClick={() => updateGoalProgress(goal.id, amount)}
                            className="rounded-full text-xs"
                          >
                            +{amount} kr
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {activeTab === 'tips' && (
          <section className="space-y-4">
            {tips.map((tip, index) => (
              <div
                key={tip.id}
                className="bg-card rounded-2xl border border-border/50 p-6 animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <h4 className="font-heading text-lg font-medium">{tip.title}</h4>
                <p className="text-muted-foreground mt-2">{tip.description}</p>
              </div>
            ))}
          </section>
        )}

        {/* Footer */}
        <div className="text-center pt-8">
          <p className="text-xs text-muted-foreground">
            Ekonomisk trygghet minskar stress och ökar livskvaliteten.
          </p>
        </div>
      </main>
    </div>
  );
}
